import { useEffect, useState } from 'preact/hooks';
import { Tooltip } from '../Tooltip';
import { AppIcon } from '../AppIcon';
import { PDF_LIMITS } from '../../../shared/pdf/limits';

export const MIN_FONT_SIZE = 7;
/** A size typed as "14", "14pt", "14 pt" or "14 points", in points; anything else isn't a size. */
export function parseFontSize(text: string): number | undefined {
  const match = /^\s*(\d+(?:\.\d+)?|\.\d+)\s*(?:pt|pts|points?)?\.?\s*$/i.exec(text);
  return match ? Number(match[1]) : undefined;
}
/** Keep a size within what a PDF field allows, to the nearest half point. */
export const clampFontSize = (size: number) => Math.min(PDF_LIMITS.maximumFontSize, Math.max(MIN_FONT_SIZE, Math.round(size * 2) / 2));
const shown = (size: number) => `${Number.isInteger(size) ? size : size.toFixed(1)} pt`;

/** The selected fields' font size, as "20 pt", with a stepper beside it. With fields of different sizes it shows the
 * largest, in gray italics, and stepping or typing sets them all from it. Clicking the size lets it be typed; Enter or
 * leaving the box applies what was typed, and text that isn't a size goes back to the last good value. Leaving without
 * typing changes nothing. The arrows, and the arrow keys while typing, step it by a point. */
export function PdfFontSize({ sizes, disabled, onSet }: { sizes: number[]; disabled?: boolean; onSet: (size: number) => void }) {
  const largest = sizes.length ? Math.max(...sizes) : undefined;
  const mixed = sizes.some(size => size !== largest);
  const [draft, setDraft] = useState<string>();
  // A change from elsewhere, such as Undo or another selection, shows at once unless the size is being typed.
  useEffect(() => { setDraft(undefined); }, [largest, mixed, sizes.length]);
  const apply = (size: number) => { if (mixed || size !== largest) onSet(size); };
  const commit = () => {
    if (draft === undefined) return;
    const size = parseFontSize(draft);
    setDraft(undefined);
    if (size !== undefined && size > 0) apply(clampFontSize(size));
  };
  const step = (by: number) => { if (largest !== undefined) { setDraft(undefined); apply(clampFontSize(largest + by)); } };
  return <div class="segmented pdf-font-size" role="group" aria-label="Font size">
    <input class={`pdf-font-size-input${mixed && draft === undefined ? ' mixed' : ''}`} aria-label="Font size in points" inputMode="decimal" disabled={disabled}
      aria-description={mixed ? 'The selected fields have different sizes; this is the largest.' : undefined}
      value={draft ?? (largest === undefined ? '' : shown(largest))}
      onFocus={e => e.currentTarget.select()} onInput={e => setDraft(e.currentTarget.value)} onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); (e.currentTarget as HTMLInputElement).select(); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setDraft(undefined); (e.currentTarget as HTMLInputElement).blur(); }
        else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); step(e.key === 'ArrowUp' ? 1 : -1); }
      }} />
    <span class="segmented-divider" aria-hidden="true" />
    <div class="pdf-font-size-stepper">
      <Tooltip text="Larger text"><button class="segmented-button" aria-label="Increase font size" disabled={disabled || (!mixed && largest! >= PDF_LIMITS.maximumFontSize)} onClick={() => step(1)}><AppIcon name="chevron-up" /></button></Tooltip>
      <Tooltip text="Smaller text"><button class="segmented-button" aria-label="Decrease font size" disabled={disabled || (!mixed && largest! <= MIN_FONT_SIZE)} onClick={() => step(-1)}><AppIcon name="chevron-down" /></button></Tooltip>
    </div>
  </div>;
}
